import {
	type Recipe,
	type ApiResponse,
	type CreateRecipeInput,
	type UpdateRecipeRequest,
	type RecipeMatch,
	type Inspiration,
	type PagedRecipes,
	type RecipeFilters,
	type Taxonomy
} from '../types';
import { request } from './http';
import { cacheGet, cacheSet, cacheClear } from './cache';

const listKey = (page: number, limit: number, filters: RecipeFilters): string =>
	`recipes:${page}:${limit}:${filters.cuisine ?? ''}:${(filters.dietary ?? []).join(',')}`;

/** Last known page of recipes, for painting a repeat visit before the fetch lands. */
const peekRecipes = (page = 1, limit = 12, filters: RecipeFilters = {}): PagedRecipes | undefined =>
	cacheGet<PagedRecipes>(listKey(page, limit, filters));

/** Last known recipe (memory only: it carries per-viewer fields like canEdit). */
const peekRecipe = (id: string): Recipe | undefined => cacheGet<Recipe>(`recipe:${id}`);

/** Last known cuisine/dietary vocabularies. */
const peekTaxonomy = (): Taxonomy | undefined => cacheGet<Taxonomy>('taxonomy');

const getRecipes = async (page = 1, limit = 12, filters: RecipeFilters = {}): Promise<PagedRecipes> => {
	const params = new URLSearchParams({ page: String(page), limit: String(limit) });
	if (filters.cuisine) params.set('cuisine', filters.cuisine);
	if (filters.dietary && filters.dietary.length > 0) params.set('dietary', filters.dietary.join(','));
	const response = await request<ApiResponse<Recipe[]>>(`/recipes/?${params.toString()}`);
	const result = {
		recipes: response.data,
		meta: response.meta || { page, limit, total: response.data.length }
	};
	// Only the signed-out-identical public listing is persisted across reloads
	cacheSet(listKey(page, limit, filters), result, true);
	return result;
};

const createRecipe = async (recipe: CreateRecipeInput): Promise<Recipe> => {
	const response = await request<ApiResponse<Recipe>>('/recipes/create', {
		method: 'POST',
		body: JSON.stringify(recipe)
	});
	cacheClear('recipe');
	return response.data;
};

/** Import a recipe from a URL, creating it for the signed-in user. */
const importRecipe = async (url: string): Promise<Recipe> => {
	const response = await request<ApiResponse<Recipe>>('/recipes/import', {
		method: 'POST',
		body: JSON.stringify({ url })
	});
	cacheClear('recipe');
	return response.data;
};

const uploadRecipeImage = async (file: FormData): Promise<string> => {
	const response = await request<ApiResponse<string>>('/recipes/create/image', {
		method: 'POST',
		body: file
	});
	return response.data;
};

const updateRecipe = async (recipe: UpdateRecipeRequest): Promise<Recipe> => {
	const { _id, ...body } = recipe;
	const response = await request<ApiResponse<Recipe>>(`/recipes/${_id}`, {
		method: 'PUT',
		body: JSON.stringify(body)
	});
	cacheClear('recipe');
	return response.data;
};

const deleteRecipe = async (id: string): Promise<void> => {
	await request<void>(`/recipes/${id}`, { method: 'DELETE' });
	cacheClear('recipe');
};

const saveRecipe = async (recipeId: string): Promise<void> => {
	await request<ApiResponse<unknown>>(`/recipes/${recipeId}/save`, { method: 'PUT' });
};

const unsaveRecipe = async (recipeId: string): Promise<void> => {
	await request<ApiResponse<unknown>>(`/recipes/${recipeId}/unsave`, { method: 'PUT' });
};

/** Rank recipes by how many of the given ingredient names they use. */
const matchByIngredients = async (ingredientNames: string[]): Promise<RecipeMatch[]> => {
	const response = await request<ApiResponse<RecipeMatch[]>>('/recipes/match', {
		method: 'POST',
		body: JSON.stringify({ ingredients: ingredientNames })
	});
	return response.data;
};

/** Pantry-powered recipe ideas for the signed-in user. */
const getInspiration = async (): Promise<Inspiration> => {
	const response = await request<ApiResponse<Inspiration>>('/recipes/inspiration');
	return response.data;
};

const getRecipeById = async (id: string): Promise<Recipe> => {
	const response = await request<ApiResponse<Recipe>>(`/recipes/${id}`);
	cacheSet(`recipe:${response.data._id}`, response.data);
	if (response.data.slug) cacheSet(`recipe:${response.data.slug}`, response.data);
	cacheSet(`recipe:${id}`, response.data);
	return response.data;
};

const search = async (query: string): Promise<Recipe[]> => {
	const response = await request<ApiResponse<Recipe[]>>(`/recipes/search?s=${encodeURIComponent(query)}`);
	return response.data;
};

/** The cuisine and dietary vocabularies recipes are classified with. */
const getTaxonomy = async (): Promise<Taxonomy> => {
	const response = await request<ApiResponse<Taxonomy>>('/recipes/taxonomy');
	cacheSet('taxonomy', response.data, true);
	return response.data;
};

const recipeService = {
	getRecipes,
	createRecipe,
	importRecipe,
	uploadRecipeImage,
	updateRecipe,
	deleteRecipe,
	saveRecipe,
	unsaveRecipe,
	matchByIngredients,
	getInspiration,
	getRecipeById,
	getTaxonomy,
	peekRecipes,
	peekRecipe,
	peekTaxonomy,
	search
};

export default recipeService;
